import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Dialog, DialogActions, DialogContent, IconButton, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import dayjs from 'dayjs';
import { Icon } from '@iconify/react/dist/iconify.js';
import apiRequest from '../../../api/request';
import RetentionThemeProvider from '../theme/RetentionThemeProvider';
import { HERO_GRADIENT, PHASE_TOKENS, RT } from '../theme/retentionTheme';
import { stageByNo } from '../constants/stages';
import { EmptyState, IconBadge, SectionCard, SoftChip } from './ui';

// Pop-up CRO Leads opens on entry when the CRO has retention work today (GET /retention/today-alerts): leads
// assigned to them today, stages scheduled for today, stages waiting on the CRO (overdue first) and flagged items. Each row opens the student's
// workspace on the stage's tab. Stays closed when there is nothing, or when the endpoint fails (schema missing).

const KIND_ICON = {
  induction: 'solar:presentation-graph-bold-duotone',
  demo: 'solar:videocamera-record-bold-duotone',
  first_session: 'solar:play-circle-bold-duotone',
  retention_call: 'solar:phone-rounded-bold-duotone',
  winback: 'solar:restart-circle-bold-duotone',
};

// date-only columns (first session, follow-ups) come back at midnight: show "Today" instead of 12:00 AM
const timeLabel = (at) => {
  const d = dayjs(at);
  if (!d.isValid()) return 'Today';
  return d.hour() === 0 && d.minute() === 0 ? 'Today' : d.format('hh:mm A');
};

const stageColor = (stageNo) => PHASE_TOKENS[stageByNo(stageNo)?.phase]?.main || RT.primary;

function AlertRow({ icon, color, title, caption, chip, onOpen }) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={1.5}
      onClick={onOpen}
      sx={{
        p: 1.25,
        borderRadius: 2.5,
        border: `1px solid ${RT.line}`,
        bgcolor: RT.paper,
        cursor: 'pointer',
        transition: 'border-color .15s, box-shadow .15s',
        '&:hover': { borderColor: alpha(color, 0.5), boxShadow: RT.shadow },
      }}
    >
      <IconBadge icon={icon} color={color} size={36} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle2" noWrap>
          {title}
        </Typography>
        <Typography variant="caption" sx={{ color: RT.muted }} noWrap component="div">
          {caption}
        </Typography>
      </Box>
      {chip}
      <Icon icon="solar:alt-arrow-right-linear" width={18} color={RT.faint} />
    </Stack>
  );
}

const Count = ({ n, color }) => <SoftChip label={n} color={color} />;

export default function TodayAlertsDialog() {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    apiRequest({ method: 'get', url: 'retention/today-alerts' })
      .then((res) => {
        if (!alive || res?.status !== 'success') return;
        const d = res.data || {};
        setAlerts(d);
        const total = ['newly_assigned', 'scheduled', 'pending', 'flagged'].reduce((n, k) => n + (d[k]?.length || 0), 0);
        if (total > 0) setOpen(true);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  if (!alerts) return null;
  const { newly_assigned: newlyAssigned = [], scheduled = [], pending = [], flagged = [], summary = {} } = alerts;

  const openStudent = (courseEnrollmentId, stageNo) => {
    const tab = stageNo ? stageByNo(stageNo)?.tab : '';
    setOpen(false);
    navigate(`/student-journey/${courseEnrollmentId}${tab ? `/${tab}` : ''}`);
  };

  const studentCaption = (row, extra) => [row.student_name, row.admn_no, extra].filter(Boolean).join(' · ');

  return (
    // theme provider outside the Dialog, same as StudentDataDialog, so the content can scroll
    <RetentionThemeProvider>
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md" scroll="paper" PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden' } }}>
        {/* hero */}
        <Box sx={{ background: HERO_GRADIENT, color: '#fff', px: 3, py: 2.25 }}>
          <Stack direction="row" alignItems="center" gap={2}>
            <IconBadge icon="solar:bell-bing-bold-duotone" color="#fff" size={52} sx={{ bgcolor: 'rgba(255,255,255,.18)', color: '#fff' }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="overline" sx={{ opacity: 0.8 }}>
                Retention · {dayjs().format('ddd, DD MMM')}
              </Typography>
              <Typography variant="h5" sx={{ color: '#fff' }} noWrap>
                Today&apos;s pending work
              </Typography>
              <Stack direction="row" gap={0.75} mt={0.75} flexWrap="wrap">
                {[
                  ['New today', summary.newly_assigned, 'solar:user-plus-bold'],
                  ['Scheduled today', summary.scheduled_today, 'solar:calendar-mark-bold'],
                  ['With you', summary.pending, 'solar:user-hand-up-bold'],
                  ['Overdue', summary.overdue, 'solar:alarm-bold'],
                  ['Due today', summary.due_today, 'solar:clock-circle-bold'],
                ].map(([label, n, icon]) => (
                  <SoftChip key={label} label={`${label} · ${n ?? 0}`} icon={icon} color="#fff" sx={{ bgcolor: 'rgba(255,255,255,.16)' }} />
                ))}
              </Stack>
            </Box>
            <IconButton onClick={() => setOpen(false)} sx={{ color: '#fff', alignSelf: 'flex-start' }}>
              <Icon icon="solar:close-circle-bold" width={26} />
            </IconButton>
          </Stack>
        </Box>

        <DialogContent dividers sx={{ bgcolor: RT.surface, p: { xs: 1.5, md: 2.5 }, overflowY: 'auto' }}>
          <Stack gap={2.5}>
            {newlyAssigned.length > 0 && (
              <SectionCard
                icon="solar:user-plus-bold-duotone"
                color={RT.success}
                title="Newly assigned today"
                action={<Count n={newlyAssigned.length} color={RT.success} />}
              >
                <Stack gap={1}>
                  {newlyAssigned.map((r) => (
                    <AlertRow
                      key={`new-${r.course_enrollment_id}`}
                      icon="solar:user-plus-bold-duotone"
                      color={RT.success}
                      title={r.student_name || r.admn_no || 'New lead'}
                      caption={[r.admn_no, `Stage ${r.stage_no} · ${stageByNo(r.stage_no)?.short || ''}`, r.assigned_by && `by ${r.assigned_by}`]
                        .filter(Boolean)
                        .join(' · ')}
                      chip={<SoftChip label={dayjs(r.assigned_on).format('hh:mm A')} icon="solar:clock-circle-bold" color={RT.success} />}
                      onOpen={() => openStudent(r.course_enrollment_id, r.stage_no)}
                    />
                  ))}
                </Stack>
              </SectionCard>
            )}

            <SectionCard
              icon="solar:calendar-mark-bold-duotone"
              color="#2563EB"
              title="Scheduled today"
              action={<Count n={scheduled.length} color="#2563EB" />}
            >
              {scheduled.length === 0 ? (
                <EmptyState icon="solar:calendar-linear" title="Nothing scheduled today" compact />
              ) : (
                <Stack gap={1}>
                  {scheduled.map((r) => (
                    <AlertRow
                      key={r.row_id}
                      icon={KIND_ICON[r.kind] || stageByNo(r.stage_no)?.icon}
                      color={stageColor(r.stage_no)}
                      title={r.label}
                      caption={studentCaption(r, `Stage ${r.stage_no} · ${stageByNo(r.stage_no)?.short || ''}`)}
                      chip={
                        Number(r.is_done) === 1 ? (
                          <SoftChip label="Done" icon="solar:check-circle-bold" color={RT.success} />
                        ) : (
                          <SoftChip label={timeLabel(r.at)} icon="solar:clock-circle-bold" color="#2563EB" />
                        )
                      }
                      onOpen={() => openStudent(r.course_enrollment_id, r.stage_no)}
                    />
                  ))}
                </Stack>
              )}
            </SectionCard>

            <SectionCard
              icon="solar:user-hand-up-bold-duotone"
              color={RT.warning}
              title="Pending stages with you"
              action={<Count n={summary.pending ?? pending.length} color={RT.warning} />}
            >
              {pending.length === 0 ? (
                <EmptyState icon="solar:check-circle-linear" title="All clear" color={RT.success} compact />
              ) : (
                <Stack gap={1}>
                  {pending.map((r) => {
                    const overdue = Number(r.is_overdue) === 1;
                    const dueToday = Number(r.is_due_today) === 1;
                    return (
                      <AlertRow
                        key={r.row_id}
                        icon={stageByNo(r.stage_no)?.icon}
                        color={overdue ? RT.error : stageColor(r.stage_no)}
                        title={r.pending_update}
                        caption={studentCaption(r, `Stage ${r.stage_no}`)}
                        chip={
                          overdue ? (
                            <SoftChip label={`Overdue · ${dayjs(r.due_at).format('DD MMM')}`} icon="solar:alarm-bold" color={RT.error} />
                          ) : dueToday ? (
                            <SoftChip label={`Due ${dayjs(r.due_at).format('hh:mm A')}`} icon="solar:clock-circle-bold" color={RT.warning} />
                          ) : r.due_at ? (
                            <SoftChip label={`Due ${dayjs(r.due_at).format('DD MMM')}`} color={RT.muted} />
                          ) : null
                        }
                        onOpen={() => openStudent(r.course_enrollment_id, r.stage_no)}
                      />
                    );
                  })}
                  {(summary.pending || 0) > pending.length && (
                    <Typography variant="caption" sx={{ color: RT.muted, textAlign: 'center' }}>
                      +{summary.pending - pending.length} more in Today&apos;s tasks
                    </Typography>
                  )}
                </Stack>
              )}
            </SectionCard>

            {flagged.length > 0 && (
              <SectionCard icon="solar:flag-bold-duotone" color="#7C3AED" title="Flagged for you" action={<Count n={flagged.length} color="#7C3AED" />}>
                <Stack gap={1}>
                  {flagged.map((r, i) => (
                    <AlertRow
                      key={`${r.course_enrollment_id}-${i}`}
                      icon="solar:flag-bold-duotone"
                      color="#7C3AED"
                      title={r.label}
                      caption={r.student_name}
                      onOpen={() => openStudent(r.course_enrollment_id)}
                    />
                  ))}
                </Stack>
              </SectionCard>
            )}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={() => setOpen(false)}>Later</Button>
          <Button
            variant="contained"
            startIcon={<Icon icon="solar:checklist-minimalistic-bold-duotone" width={18} />}
            onClick={() => {
              setOpen(false);
              navigate('/student-journey/tasks');
            }}
          >
            Open Today&apos;s tasks
          </Button>
        </DialogActions>
      </Dialog>
    </RetentionThemeProvider>
  );
}
